import apiClient from '@/lib/api-client';

// Dashboard Types
export interface StaffStats {
  total_bookings: number;
  completed_bookings: number;
  cancelled_bookings: number;
  today_bookings_count: number;
  monthly_revenue: number;
  upcoming_bookings_count: number;
  cancellation_rate: number;
  completion_rate: number;
  period: {
    start: string;
    end: string;
  };
}

export interface StaffBooking {
  id: number;
  public_id: string;
  booking_code: string;
  status: string;
  customer_id?: number | null;
  customer_first_name: string;
  customer_last_name: string;
  customer_full_name: string;
  customer_email: string;
  customer_phone: string;
  service_name: string;
  notes?: string;
  start_time: string;
  end_time: string;
  available_slot_id?: number | null;
  available_slot_current_bookings?: number | null;
  custom_answers?: Record<string, string | string[]>;
  /** Present in psychotherapy practices: the booking's session-note status (content is author-only). */
  session_note?: { status: 'draft' | 'completed' | null; id: string | null; mine: boolean; can_write: boolean } | null;
  service: {
    id: number;
    name: string;
    duration_minutes: number;
    price: number;
    // Capacity > 1 means a group class; the schedule renders these via the
    // group-class slot block (with N/M counter), not as individual booking
    // blocks. Plain bookings list still shows them.
    max_capacity?: number;
    custom_booking_questions?: Array<{
      id: string;
      question: string;
      type: string;
      required: boolean;
      options?: string[];
    }>;
  } | null;
  provider?: {
    id: number;
    name: string;
    display_name?: string;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface TodayAppointments {
  date: string;
  count: number;
  bookings: StaffBooking[];
}

export interface UpcomingAppointmentsByDate {
  date: string;
  count: number;
  bookings: StaffBooking[];
}

export interface UpcomingAppointments {
  period: {
    start: string;
    end: string;
  };
  total_count: number;
  dates: UpcomingAppointmentsByDate[];
}

// Slot Types
export interface StaffAvailableSlot {
  id: number;
  start_time: string;
  end_time: string;
  available: boolean;
  booked: boolean;
  reserved: boolean;
  cancelled?: boolean;
  cancelled_at?: string | null;
  // Capacity counters — only meaningful when service.max_capacity > 1
  // (group classes). For regular 1-on-1 slots both are 0/1.
  max_capacity?: number;
  current_bookings?: number;
  service: {
    id: number;
    name: string;
    duration_minutes: number;
    max_capacity?: number | null;
  } | null;
  booking: {
    id: number;
    public_id: string;
    status: string;
    customer_full_name: string;
    service_name?: string;
  } | null;
}

export interface StaffAvailableSlotDetail extends StaffAvailableSlot {
  duration_minutes: number;
  formatted_time_range: string;
  formatted_date: string;
  created_at: string;
  updated_at: string;
  service: {
    id: number;
    name: string;
    description?: string;
    duration_minutes: number;
    price: number;
    color?: string;
  } | null;
  provider: {
    id: number;
    display_name: string;
    title?: string;
    color?: string;
  } | null;
  booking: {
    id: number;
    public_id: string;
    status: string;
    customer_first_name: string;
    customer_last_name: string;
    customer_full_name: string;
    customer_email: string;
    customer_phone: string;
    notes?: string;
    confirmed_at?: string;
    created_at: string;
  } | null;
  actions_available: {
    can_block: boolean;
    can_unblock: boolean;
  };
}

// Filter Types
export interface BookingFilters {
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  search?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  per_page?: number;
}

export interface SlotFilters {
  available?: boolean;
  booked?: boolean;
  start_date?: string;
  end_date?: string;
  date?: string;
  period?: 'future' | 'past';
  page?: number;
  per_page?: number;
}

// Slot Creation Types
export interface CreateSlotData {
  service_id: number;
  start_time: string;
  end_time: string;
  available?: boolean;
  max_capacity?: number;
  notes?: string;
}

export interface TimeSlot {
  start_time: string;
  end_time?: string;
}

export interface GenerateRecurringSlotsData {
  service_id: number;
  start_date: string;
  end_date: string;
  days_of_week: string[];
  time_slots: TimeSlot[];
  max_capacity?: number;
}

export interface GenerateFromTimeRangeData {
  service_id: number;
  date: string;
  start_time: string;
  end_time: string;
  max_capacity?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    total_pages: number;
    total_count: number;
    filters_applied?: Record<string, any>;
  };
}

export interface CalendarBlock {
  id: number;
  start_time: string;
  end_time: string;
  reason: string;
  notes: string | null;
  from_calendar: boolean;
  calendar_event_id: number | null;
}

export class StaffService {
  // Dashboard endpoints

  async getDashboardStats(): Promise<StaffStats> {
    const { data } = await apiClient.get<{ data: StaffStats }>('/v1/staff/dashboard/stats');
    return data.data;
  }

  async getTodayAppointments(): Promise<TodayAppointments> {
    const { data } = await apiClient.get<{ data: TodayAppointments }>('/v1/staff/dashboard/today');
    return data.data;
  }

  async getUpcomingAppointments(): Promise<UpcomingAppointments> {
    const { data } = await apiClient.get<{ data: UpcomingAppointments }>('/v1/staff/dashboard/upcoming');
    return data.data;
  }

  // Bookings endpoints

  async getBookings(filters?: BookingFilters): Promise<PaginatedResponse<StaffBooking>> {
    const { data } = await apiClient.get<{ data: StaffBooking[]; meta: any }>('/v1/staff/bookings', {
      params: filters
    });

    return {
      data: data.data,
      meta: data.meta
    };
  }

  async getBooking(id: number | string): Promise<StaffBooking> {
    const { data } = await apiClient.get<{ data: StaffBooking }>(`/v1/staff/bookings/${id}`);
    return data.data;
  }

  async confirmBooking(id: number | string): Promise<{ booking: StaffBooking; message: string }> {
    const { data } = await apiClient.post<{ data: { booking: StaffBooking; message: string } }>(
      `/v1/staff/bookings/${id}/confirm`
    );
    return data.data;
  }

  async cancelBooking(id: number | string, reason?: string): Promise<{ booking: StaffBooking; message: string }> {
    const { data } = await apiClient.post<{ data: { booking: StaffBooking; message: string } }>(
      `/v1/staff/bookings/${id}/cancel`,
      { reason }
    );
    return data.data;
  }

  async completeBooking(id: number | string): Promise<{ booking: StaffBooking; message: string }> {
    const { data } = await apiClient.post<{ data: { booking: StaffBooking; message: string } }>(
      `/v1/staff/bookings/${id}/complete`
    );
    return data.data;
  }

  async markNoShow(id: number | string): Promise<{ booking: StaffBooking; message: string }> {
    const { data } = await apiClient.post<{ data: { booking: StaffBooking; message: string } }>(
      `/v1/staff/bookings/${id}/no_show`
    );
    return data.data;
  }

  // Available Slots endpoints

  async getAvailableSlots(filters?: SlotFilters): Promise<PaginatedResponse<StaffAvailableSlot>> {
    const { data } = await apiClient.get<{ data: StaffAvailableSlot[]; meta: any }>(
      '/v1/staff/available_slots',
      { params: filters }
    );

    return {
      data: data.data,
      meta: data.meta
    };
  }

  async getAvailableSlot(id: number): Promise<StaffAvailableSlotDetail> {
    const { data } = await apiClient.get<{ data: StaffAvailableSlotDetail }>(`/v1/staff/available_slots/${id}`);
    return data.data;
  }

  async blockSlot(id: number): Promise<{ slot: StaffAvailableSlotDetail; message: string }> {
    const { data } = await apiClient.post<{ data: { slot: StaffAvailableSlotDetail; message: string } }>(
      `/v1/staff/available_slots/${id}/block`
    );
    return data.data;
  }

  async unblockSlot(id: number): Promise<{ slot: StaffAvailableSlotDetail; message: string }> {
    const { data } = await apiClient.post<{ data: { slot: StaffAvailableSlotDetail; message: string } }>(
      `/v1/staff/available_slots/${id}/unblock`
    );
    return data.data;
  }

  // Slot Creation Methods

  async createSlot(slotData: CreateSlotData): Promise<{ slot: StaffAvailableSlotDetail; message: string; warning?: { type: string; message: string } }> {
    const { data } = await apiClient.post<{ data: { slot: StaffAvailableSlotDetail; message: string; warning?: { type: string; message: string } } }>(
      '/v1/staff/available_slots',
      { available_slot: slotData }
    );
    return data.data;
  }

  async deleteSlot(id: number): Promise<{ message: string }> {
    const { data } = await apiClient.delete<{ data: { message: string } }>(
      `/v1/staff/available_slots/${id}`
    );
    return data.data;
  }

  async generateRecurringSlots(params: GenerateRecurringSlotsData): Promise<{ slots: StaffAvailableSlotDetail[]; count: number; message: string }> {
    const { data } = await apiClient.post<{ data: { slots: StaffAvailableSlotDetail[]; count: number; message: string } }>(
      '/v1/staff/available_slots/generate_recurring',
      params
    );
    return data.data;
  }

  async generateFromTimeRange(params: GenerateFromTimeRangeData): Promise<{ slots: StaffAvailableSlotDetail[]; count: number; message: string }> {
    const { data } = await apiClient.post<{ data: { slots: StaffAvailableSlotDetail[]; count: number; message: string } }>(
      '/v1/staff/available_slots/generate_from_time_range',
      params
    );
    return data.data;
  }

  async getCalendarBlocks(startDate: string, endDate: string): Promise<CalendarBlock[]> {
    const { data } = await apiClient.get<{ data: CalendarBlock[] }>(
      '/v1/staff/available_slots/calendar_blocks',
      { params: { start_date: startDate, end_date: endDate } }
    );
    return data.data;
  }
}

export const staffService = new StaffService();
